import { Ruler } from "lucide-react";
import {
  dimBindingId,
  type DimAnchor,
  type DimBinding,
  type DimDirection,
  type DimRef,
  type DimShape,
  type DimTerminal,
} from "sketch/shapes/dim";
import type { GridlineShape } from "sketch/shapes/gridline";
import {
  StateNode,
  Vec,
  createShapeId,
  maybeSnapToGrid,
  type TLBindingId,
  type TLGeoShape,
  type TLLineShape,
  type TLShape,
  type TLShapeId,
  type TLUiIconJsx,
  type TLUiOverrides,
  type TLUiToolItem,
} from "tldraw";

import {
  dimBindingsOf,
  dimEndpointPage,
  dimLayout,
  dimOffset,
  offsetGrowthUnit,
} from "./dim-geometry";

// Not `tlui-icon`: that class masks a background color, blanking JSX icons.
const DimIcon: TLUiIconJsx = (
  <div style={{ width: 18, height: 18 }}>
    <Ruler className="h-full w-full" strokeWidth={1.75} />
  </div>
);

const HIT_MARGIN = 8;
const EPSILON = 1e-6;
/** A dim measures the drawing, not the annotation on it: a leader or another
 *  dim stands where the sheet's layout put it, so measuring to one measures
 *  nothing. The callout tool refuses the same two. */
const NOT_MEASURABLE = new Set(["dim", "callout"]);
/** Screen pixels within which the placement click keeps the sheet's own
 *  stacked line instead of authoring one. */
const STACK_SNAP = 10;

/**
 * One reference the dim measures to. `anchor`/`vertex` pin a point on the
 * target; `edge` makes it a whole edge, which measures across rather than to
 * a corner. `ends` are that edge's ends in page space, which is what lets a
 * lone edge dimension itself.
 */
interface Pick {
  point: Vec;
  target?: TLShape;
  anchor?: { x: number; y: number };
  vertex?: string;
  edge?: DimAnchor;
  ends?: [Vec, Vec];
  /** The direction a dim to this reference takes, when the reference says so:
   *  you measure across a face or a gridline, not along it. */
  axis?: DimDirection;
}

/** The string this run continues, when it started from a landed dim's end. */
interface Seed {
  chain: string;
  direction: DimDirection;
  ref: DimRef;
}

/**
 * The dimension tool. One rule: every click picks a reference, and a click on
 * empty canvas places the dimension once it has enough of them. That is how
 * a parametric sketcher works, and it is what makes every drafting move one
 * flow instead of a mode:
 *
 * - Click a rectangle's side or a polyline segment: that edge alone measures
 *   itself, so a rectangle is dimensioned a side per click. Click it again
 *   (or double-click it) to drop it on the sheet's own stacked line.
 * - Click one wall face, then another: the dim measures between them, square
 *   to the faces however they run.
 * - Keep clicking references and they string together as one chain.
 * - A click on empty canvas places the dim line where you clicked (that
 *   authors its offset); Enter places it on the stacked line instead.
 * - Whatever the cursor would pick is highlighted, and the live preview shows
 *   the dim you would get. Escape cancels the run.
 */
export class DimTool extends StateNode {
  static override id = "dim";
  /** Ties the tool to the dim shape's style props, so the style panel shows
   *  the dim-reference row while the tool is active. */
  override shapeType = "dim";

  private picks: Pick[] = [];
  /** The provisional dims, one per consecutive pair of references. */
  private links: TLShapeId[] = [];
  private seed: Seed | null = null;
  /** Where the sheet stacks this run's dim line, along its growth direction,
   *  so a placement click landing on it stays in the stack. */
  private autoLine: number | null = null;

  override onEnter() {
    this.reset();
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }

  override onExit() {
    this.cancel();
    this.editor.setHintingShapes([]);
    this.editor.setCursor({ type: "default", rotation: 0 });
  }

  override onKeyDown(info: { key: string }) {
    if (info.key === "Enter" && this.links.length) this.place(null);
  }

  /** Escape drops the run being drawn and leaves the tool ready for the next
   *  one; a second Escape, with nothing in hand, leaves the tool. */
  override onCancel() {
    if (this.picks.length) {
      this.cancel();
      return;
    }
    this.editor.setCurrentTool("select");
  }

  override onPointerMove() {
    const hover = this.pickAt();
    this.editor.setHintingShapes(hover.target ? [hover.target.id] : []);
    if (!this.picks.length) {
      const endpoint = this.dimEndpointAt();
      // Chaining affordance: a landed dim's end lights up when the next link
      // of its string could start there.
      if (endpoint) this.editor.setHintingShapes([endpoint.dim.id]);
      return;
    }
    // Over a reference the next link previews; over empty canvas the dim
    // line follows the cursor, which is the placement it would land at.
    const previewing = Boolean(hover.target) || !this.isComplete();
    const wanted = this.refs().length - (previewing ? 0 : 1);
    if (wanted !== this.links.length) this.rebuild(previewing ? hover : null);
    if (previewing) this.trackCursor(hover);
    else this.placeAt(hover.point);
  }

  override onPointerDown() {
    if (!this.picks.length) {
      const endpoint = this.dimEndpointAt();
      if (endpoint) {
        this.startFrom(endpoint.dim, endpoint.terminal);
        return;
      }
    }
    const pick = this.pickAt();
    const last = this.picks.at(-1);
    // Picking the same reference twice ends the run rather than measuring
    // nothing, which is what makes a double-click on an edge one gesture.
    if (last && samePick(last, pick)) {
      if (this.isComplete()) this.place(null);
      return;
    }
    if (!pick.target && this.isComplete()) {
      this.place(pick.point);
      return;
    }
    this.picks.push(pick);
    this.rebuild(null);
  }

  private reset() {
    this.picks = [];
    this.links = [];
    this.seed = null;
    this.autoLine = null;
  }

  private cancel() {
    if (this.links.length) this.editor.deleteShapes(this.links);
    this.reset();
  }

  /** The references the picks amount to. One edge on its own measures itself,
   *  end to end; with anything else picked it is a reference like any other.
   *  Its ends run west to east (north to south when it stands up), so the dim
   *  reads the way a drafter letters it and grows from the end a plan is
   *  measured from, whichever way round the shape stores its corners. */
  private refs(): Pick[] {
    const [only] = this.picks;
    if (this.picks.length !== 1 || !only?.edge || !only.ends) return this.picks;
    // Measured along the edge, not across it, which is the one place the two
    // read differently.
    const axis = alongEdge(only.ends);
    const ends: Pick[] = [
      {
        point: only.ends[0],
        target: only.target,
        anchor: only.anchor,
        vertex: only.vertex,
        axis,
      },
      {
        point: only.ends[1],
        target: only.target,
        anchor: only.edge.anchor,
        vertex: only.edge.vertex,
        axis,
      },
    ];
    return leadingEnd(ends[0]!.point, ends[1]!.point) ? ends : ends.reverse();
  }

  private isComplete(): boolean {
    return this.refs().length >= 2;
  }

  /** The dim whose dim-line end sits under the cursor, if any. */
  private dimEndpointAt(): { dim: DimShape; terminal: DimTerminal } | null {
    const point = this.editor.inputs.currentPagePoint;
    const threshold = 12 / this.editor.getZoomLevel();
    for (const shape of this.editor.getCurrentPageShapes()) {
      if (shape.type !== "dim" || this.links.includes(shape.id)) continue;
      const dim = shape as DimShape;
      const { lineA, lineB } = dimLayout(this.editor, dim);
      if (Vec.Dist(lineA, point) <= threshold)
        return { dim, terminal: "start" };
      if (Vec.Dist(lineB, point) <= threshold) return { dim, terminal: "end" };
    }
    return null;
  }

  /** Continue a landed dim's string: the next link starts where it ended,
   *  on the same line, sharing the terminator that reads as one chain. */
  private startFrom(dim: DimShape, terminal: DimTerminal): void {
    // The chain id is authored on first use: the source dim names it.
    let chain = dim.props.chain;
    if (!chain) {
      chain = dim.id.replace(/^shape:/, "");
      this.editor.updateShape<DimShape>({
        id: dim.id,
        type: "dim",
        props: { chain },
      });
    }
    this.seed = {
      chain,
      direction: dim.props.direction,
      ref: terminal === "start" ? dim.props.refStart : dim.props.refEnd,
    };
    const binding = dimBindingsOf(this.editor, dim.id)[terminal];
    this.picks = [
      {
        point: dimEndpointPage(this.editor, dim, terminal),
        target: binding && (this.editor.getShape(binding.toId) ?? undefined),
        anchor: binding?.props.anchor,
        vertex: binding?.props.vertex,
        edge: binding?.props.edge,
      },
    ];
    this.rebuild(null);
  }

  private pickAt(): Pick {
    const point = Vec.From(this.editor.inputs.currentPagePoint);
    const target = this.editor.getShapeAtPoint(point, {
      hitInside: false,
      hitLocked: true, // locked gridlines still take dim references
      margin: HIT_MARGIN,
      filter: (shape) => !NOT_MEASURABLE.has(shape.type),
    });
    // A miss stays free but lands on the graph paper.
    if (!target) return { point: maybeSnapToGrid(point, this.editor) };
    const bounds = this.editor.getShapePageBounds(target.id);
    if (!bounds) return { point };
    if (target.type === "gridline") {
      const axis = (target as GridlineShape).props.axis;
      // On a gridline the meaningful coordinate is the line's own; anchor
      // to the clicked spot along it so the dim lands where the user aimed.
      return {
        point,
        target,
        axis,
        anchor: {
          x: axis === "x" ? 0.5 : clamp01((point.x - bounds.x) / bounds.w),
          y: axis === "y" ? 0.5 : clamp01((point.y - bounds.y) / bounds.h),
        },
      };
    }
    const edge = this.edgeAt(target, point);
    if (edge) return { point, target, ...edge, axis: acrossEdge(edge.ends) };
    return {
      point,
      target,
      anchor: {
        x: bounds.w ? clamp01((point.x - bounds.x) / bounds.w) : 0.5,
        y: bounds.h ? clamp01((point.y - bounds.y) / bounds.h) : 0.5,
      },
    };
  }

  /** The edge under the cursor: a polyline segment (pinned by the vertex ids
   *  that make its references durable) or a rectangle side. Other shapes have
   *  no edges to measure, so a click on one picks a point on its bounds. */
  private edgeAt(shape: TLShape, click: Vec): Edge | null {
    let best: { edge: Edge; distance: number } | null = null;
    for (const edge of this.edgesOf(shape)) {
      const distance = Vec.DistanceToLineSegment(
        edge.ends[0],
        edge.ends[1],
        click,
      );
      if (!best || distance < best.distance) best = { edge, distance };
    }
    return best?.edge ?? null;
  }

  private edgesOf(shape: TLShape): Edge[] {
    const outline = this.outlineOf(shape);
    const bounds = this.editor.getShapePageBounds(shape.id);
    if (!outline || !bounds) return [];
    const { corners, closed } = outline;
    // A binding anchors to a fraction of the target's page bounds, so every
    // corner is normalized there whatever space its own shape keeps it in.
    // A rotated rectangle would otherwise bind to its bounding box and
    // measure a side it does not have.
    const anchorOf = (page: Vec) => ({
      x: bounds.w ? clamp01((page.x - bounds.x) / bounds.w) : 0.5,
      y: bounds.h ? clamp01((page.y - bounds.y) / bounds.h) : 0.5,
    });
    const count = (closed ? corners.length : corners.length - 1) || 0;
    return Array.from({ length: Math.max(count, 0) }, (_, i) => {
      const a = corners[i]!;
      const b = corners[(i + 1) % corners.length]!;
      return {
        anchor: anchorOf(a.page),
        ...(a.vertex ? { vertex: a.vertex } : {}),
        edge: {
          anchor: anchorOf(b.page),
          ...(b.vertex ? { vertex: b.vertex } : {}),
        },
        ends: [a.page, b.page] as [Vec, Vec],
      };
    });
  }

  /** The corners a shape's edges run between, in page space, each carrying
   *  the vertex it pins when the shape names one. A rectangle's outline
   *  closes back on itself; a polyline's does not. */
  private outlineOf(
    shape: TLShape,
  ): { corners: { page: Vec; vertex?: string }[]; closed: boolean } | null {
    const transform = this.editor.getShapePageTransform(shape.id);
    if (!transform) return null;
    const at = (point: { x: number; y: number }) =>
      Vec.From(transform.applyToPoint(point));
    if (shape.type === "line") {
      const points = Object.values((shape as TLLineShape).props.points).sort(
        (p, q) => (p.index < q.index ? -1 : 1),
      );
      return {
        corners: points.map((point) => ({ page: at(point), vertex: point.id })),
        closed: false,
      };
    }
    // Only a rectangle has sides its bounds can stand in for.
    if (shape.type !== "geo" || (shape as TLGeoShape).props.geo !== "rectangle")
      return null;
    const { w, h } = (shape as TLGeoShape).props;
    if (!w && !h) return null;
    return {
      corners: [
        { x: 0, y: 0 },
        { x: w, y: 0 },
        { x: w, y: h },
        { x: 0, y: h },
      ].map((corner) => ({ page: at(corner) })),
      closed: true,
    };
  }

  /**
   * Reconcile the provisional dims to the picked references (plus `hover`,
   * the one the cursor is over). Every link of a run shares one direction and
   * one chain, because a string of dims is one line on the sheet.
   */
  private rebuild(hover: Pick | null): void {
    const refs = this.refs();
    const all = hover ? [...refs, hover] : refs;
    const wanted = Math.max(all.length - 1, 0);
    if (this.links.length > wanted) {
      this.editor.deleteShapes(this.links.slice(wanted));
      this.links = this.links.slice(0, wanted);
    }
    while (this.links.length < wanted) {
      const id = createShapeId();
      this.editor.markHistoryStoppingPoint(`dim:${id}`);
      this.editor.createShape<DimShape>({
        id,
        type: "dim",
        x: 0,
        y: 0,
        props: {
          ...emptyDimProps(),
          ...(this.seed ? { refStart: this.seed.ref } : {}),
        },
      });
      this.links.push(id);
    }
    if (!wanted) {
      this.autoLine = null;
      return;
    }
    const direction = this.directionOf(all);
    const chain =
      this.seed?.chain ??
      (wanted > 1 ? this.links[0]!.replace(/^shape:/, "") : null);
    this.links.forEach((id, i) => {
      const [a, b] = [all[i]!, all[i + 1]!];
      this.editor.updateShape<DimShape>({
        id,
        type: "dim",
        props: {
          direction,
          chain,
          offset: null,
          start: { x: a.point.x, y: a.point.y },
          end: { x: b.point.x, y: b.point.y },
        },
      });
      // The hovered reference is not picked yet, so it stays a loose point:
      // only committed references become bindings.
      this.bind(id, "start", i < refs.length ? a : null);
      this.bind(id, "end", i + 1 < refs.length ? b : null);
    });
    this.autoLine = this.lineOf(this.links[0]!);
  }

  /** Which way the whole run measures. A string is one line, so the first
   *  two references settle it (a continued string inherits it outright). */
  private directionOf(refs: Pick[]): DimDirection {
    const [a, b] = refs;
    if (this.seed) return this.seed.direction;
    return a && b ? inferDirection(a, b) : "x";
  }

  /** Follow the cursor with the trailing link, which has no reference yet.
   *  Its direction follows too: until the second reference is picked, aiming
   *  is what says whether this dim runs across or up the page. */
  private trackCursor(hover: Pick): void {
    const id = this.links.at(-1);
    if (!id) return;
    this.editor.updateShape<DimShape>({
      id,
      type: "dim",
      props: {
        direction: this.directionOf([...this.refs(), hover]),
        end: { x: hover.point.x, y: hover.point.y },
      },
    });
  }

  private bind(dimId: TLShapeId, terminal: DimTerminal, pick: Pick | null) {
    const id = dimBindingId(dimId, terminal) as TLBindingId;
    const existing = this.editor.getBinding(id) as DimBinding | undefined;
    const target = pick?.target;
    if (!target) {
      if (existing) this.editor.deleteBinding(id);
      return;
    }
    const props: DimBinding["props"] = {
      terminal,
      anchor: pick.anchor ?? { x: 0.5, y: 0.5 },
      ...(pick.vertex ? { vertex: pick.vertex } : {}),
      ...(pick.edge ? { edge: pick.edge } : {}),
    };
    // Rebinding the same reference every frame would republish the dim, so
    // an unchanged binding is left alone.
    if (
      existing &&
      existing.toId === target.id &&
      sameAnchor(existing.props, props) &&
      sameAnchor(existing.props.edge, props.edge)
    ) {
      return;
    }
    if (existing) this.editor.deleteBinding(id);
    this.editor.createBindings<DimBinding>([
      {
        // The deterministic per-terminal id every write path shares, so a
        // later re-bind (from the agent or the UI) replaces, never stacks.
        id,
        type: "dim",
        fromId: dimId,
        toId: target.id,
        props,
      },
    ]);
  }

  /** Where a link's dim line sits, along the direction its offset grows. */
  private lineOf(id: TLShapeId): number | null {
    const link = this.editor.getShape<DimShape>(id);
    if (!link) return null;
    // Dim shapes keep x/y at 0, so their local space is page space.
    return dimLayout(this.editor, link).lineA.dpr(
      offsetGrowthUnit(this.editor, link),
    );
  }

  /** Put the run's dim line through `at`, or leave it stacked when the click
   *  lands on the line the sheet chose for it. */
  private placeAt(at: Vec): void {
    const first =
      this.links[0] && this.editor.getShape<DimShape>(this.links[0]);
    if (!first) return;
    const want = at.dpr(offsetGrowthUnit(this.editor, first));
    const stacked =
      this.autoLine !== null &&
      Math.abs(want - this.autoLine) <= STACK_SNAP / this.editor.getZoomLevel();
    for (const id of this.links) {
      const link = this.editor.getShape<DimShape>(id);
      const line = this.lineOf(id);
      if (!link || line === null) continue;
      const offset = stacked
        ? null
        : dimOffset(this.editor, link) + (want - line);
      if (offset === link.props.offset) continue;
      this.editor.updateShape<DimShape>({ id, type: "dim", props: { offset } });
    }
  }

  /** Give the run back to the sheet's own stacking. */
  private stack(): void {
    for (const id of this.links) {
      if (this.editor.getShape<DimShape>(id)?.props.offset === null) continue;
      this.editor.updateShape<DimShape>({
        id,
        type: "dim",
        props: { offset: null },
      });
    }
  }

  /** Land the run. `at` authors where the dim line goes; null hands the run
   *  back to the sheet, which stacks it outside the drawing. */
  private place(at: Vec | null): void {
    if (at) this.placeAt(at);
    else this.stack();
    const [first, ...rest] = this.links;
    this.reset();
    // The usual next move is typing the intended dimension, but only when
    // there is one of them: a string is roughed in first and adjusted after.
    if (first && !rest.length) this.editor.setEditingShape(first);
  }
}

/** A pickable edge of a shape: its two ends, each in the terms a binding
 *  stores them in, plus where they land on the page. */
interface Edge {
  anchor: { x: number; y: number };
  vertex?: string;
  edge: DimAnchor;
  ends: [Vec, Vec];
}

function emptyDimProps(): Partial<DimShape["props"]> {
  return {
    value: null,
    direction: "x",
    offset: null,
    chain: null,
    start: { x: 0, y: 0 },
    end: { x: 0, y: 0 },
  };
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function sameAnchor(
  a: DimAnchor | undefined,
  b: DimAnchor | undefined,
): boolean {
  if (!a || !b) return !a && !b;
  return (
    a.anchor.x === b.anchor.x &&
    a.anchor.y === b.anchor.y &&
    a.vertex === b.vertex
  );
}

/** Whether `a` is the end a dim should start from: the west one, or the
 *  north one when the two stand level. */
function leadingEnd(a: Vec, b: Vec): boolean {
  return Math.abs(b.x - a.x) < EPSILON ? a.y <= b.y : a.x < b.x;
}

function samePick(a: Pick, b: Pick): boolean {
  if (a.target?.id !== b.target?.id) return false;
  if (!a.target) return Vec.Dist(a.point, b.point) < EPSILON;
  return (
    a.vertex === b.vertex &&
    a.anchor?.x === b.anchor?.x &&
    a.anchor?.y === b.anchor?.y &&
    sameAnchor(a.edge, b.edge)
  );
}

/** Measuring across an edge: a face that stands up is measured east-west. */
function acrossEdge([a, b]: [Vec, Vec]): DimDirection {
  if (Math.abs(b.x - a.x) < EPSILON) return "x";
  if (Math.abs(b.y - a.y) < EPSILON) return "y";
  return "aligned";
}

/** Measuring along an edge, which is what an edge on its own measures. */
function alongEdge([a, b]: [Vec, Vec]): DimDirection {
  if (Math.abs(b.y - a.y) < EPSILON) return "x";
  if (Math.abs(b.x - a.x) < EPSILON) return "y";
  return "aligned";
}

/**
 * Which way a dim measures. A reference that knows says so, and two that
 * agree settle it. Two loose points cannot say, so the aim decides, and a
 * plan is dimensioned east-west or north-south rather than on the rake.
 */
function inferDirection(a: Pick, b: Pick): DimDirection {
  const axis =
    a.axis && b.axis ? (a.axis === b.axis ? a.axis : null) : (a.axis ?? b.axis);
  if (axis) return axis;
  const delta = Vec.Sub(b.point, a.point);
  return Math.abs(delta.x) >= Math.abs(delta.y) ? "x" : "y";
}

export const dimToolOverride: TLUiOverrides = {
  translations: {
    en: { "tool.dim": "Dimension" },
  },
  tools(editor, tools) {
    tools.dim = {
      id: DimTool.id,
      label: "tool.dim",
      icon: DimIcon,
      // "d" is dimension in CAD muscle memory (Revit's DI family). tldraw's
      // pencil loses the alias but keeps b (brush) and x, and gains p (pen).
      kbd: "d",
      onSelect() {
        editor.setCurrentTool(DimTool.id);
      },
    } satisfies TLUiToolItem;
    if (tools.draw) tools.draw.kbd = "b,x,p";
    return tools;
  },
};
